import { FC, useContext } from "react";
import { StyledCaption, StyledImage } from "./ArticleImage.styled";
import {
  CropObject,
  imageHeroUrl,
  imageHeroUrlPortrait,
} from "common/Utils/images";
import { PublicationContext } from "common/context/Publication";
import { publicationTypes } from "common/context/Publication/PublicationContext";

export interface Props {
  image: {
    url: string;
    caption?: string;
    description?: string;
    credit?: string;
    copyright?: string;
    localCopyright?: string;
    localCaption?: string;
    extra?: {
      imageSize?: {
        width: number;
        height: number;
      };
      sizes?: {
        threebytwo?: CropObject;
        Freeform?: {
          cropData: {
            height: number;
            rotate: number;
            scaleX: number;
            scaleY: number;
            width: number;
            x: number;
            y: number;
          };
          trimData: {
            bottom: number;
            left: number;
            right: number;
            top: number;
          };
        };
      };
    };
    crop?: CropObject;
  };
  type?: string;
  isHeroOverride?: boolean;
  isHero?: boolean;
}

const ArticleImage: FC<Props> = ({
  type,
  image,
  isHeroOverride,
  isHero = false,
}) => {
  const { publication } = useContext<publicationTypes>(PublicationContext);
  const url = image ? image.url : "/images/placeholder.jpg";

  let cropData

  // Check if Freeform exists and if cropData has been reset (trimData values are all 0)
  const freeformData = image?.extra?.sizes?.Freeform
  const isCropDataReset = freeformData?.trimData
    ? freeformData.trimData.top === 0 &&
      freeformData.trimData.right === 0 &&
      freeformData.trimData.bottom === 0 &&
      freeformData.trimData.left === 0
    : false

  const hasFreeformCrop = freeformData !== undefined && !isCropDataReset

  // If Hero image has freeform crop data, then set cropData to the freeform crop
  if (isHero && hasFreeformCrop) {
    cropData = {
      ...image?.extra?.sizes?.Freeform,
      name: "Freeform",
    } as CropObject;
  } else if (image?.crop && image?.crop?.name) {
    cropData = { ...image.crop, name: image.crop.name } as CropObject;
  } else {
    cropData = {
      ...image?.extra?.sizes?.threebytwo,
      name: "threebytwo",
    } as CropObject;
  }

  const caption = image?.caption;
  const description = image?.description;
  const credit = image?.credit;
  const copyright = image?.copyright;
  const localCopyright = image?.localCopyright;
  const localCaption = image?.localCaption ? image?.localCaption : "";
  const isLocalCaptionHTML =
    localCaption && /<\/?[a-z][\s\S]*>/i.test(localCaption);
  const isCaptionHTML = caption && /<\/?[a-z][\s\S]*>/i.test(caption);

  const isPortraitImage =
    image?.extra?.imageSize?.width && image?.extra?.imageSize?.height
      ? image?.extra?.imageSize.height > image?.extra?.imageSize.width
      : false;
  // If Hero image is portrait and has no freeform crop override,
  // or isHeroOverride is true, then show full image with grey bars
  const usePortraitHero =
    (isHero && isPortraitImage && !hasFreeformCrop) || isHeroOverride;

  return (
    <>
      <StyledImage
        image_type={type}
        isHero={isHero}
        srcSet={`
            ${
              usePortraitHero
                ? imageHeroUrlPortrait(publication, url, cropData, true)
                : imageHeroUrl(publication, url, cropData, true)
            }   540w,
            ${
              usePortraitHero
                ? imageHeroUrlPortrait(publication, url, cropData, true)
                : imageHeroUrl(publication, url, cropData, false)
            }   840w,
        `}
        src={
          usePortraitHero
            ? imageHeroUrlPortrait(publication, url, cropData, true)
            : imageHeroUrl(publication, url, cropData)
        }
        alt={description && description}
      />

      <StyledCaption>
        {localCaption ? (
          isLocalCaptionHTML ? (
            <>{localCaption.replace(/<[^>]*>?/gm, "")}</>
          ) : (
            localCaption
          )
        ) : caption && isCaptionHTML ? (
          caption.replace(/<[^>]*>?/gm, "")
        ) : (
          caption
        )}
        <span>
          {credit && <>Credit by ({credit})</>}
          {copyright && <> ({copyright})</>}
          {localCopyright && !copyright && <> ({localCopyright})</>}
        </span>
      </StyledCaption>
    </>
  );
};

export default ArticleImage;
